export type DateQualifier = 'exact' | 'about' | 'before' | 'after';

export interface PartialDate {
  year?: number | null;
  month?: number | null;
  day?: number | null;
  qualifier?: DateQualifier | null;
}

export const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'] as const;
export const MONTHS_ES_LONG = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const;

const QUALIFIER_PREFIX: Record<DateQualifier, string> = { exact: '', about: 'c. ', before: 'antes de ', after: 'después de ' };

export function formatPartialDate(d: PartialDate | null | undefined): string {
  if (!d || !d.year) return '';
  const parts: string[] = [];
  if (d.month && d.day) parts.push(String(d.day));
  if (d.month) parts.push(MONTHS_ES[d.month - 1] ?? '');
  parts.push(String(d.year));
  return QUALIFIER_PREFIX[d.qualifier ?? 'exact'] + parts.join(' ');
}

/** Valida que la fecha parcial sea coherente (mes sin año, día sin mes, 31 de febrero, etc.). */
export function isValidPartialDate(d: PartialDate): boolean {
  if (d.month != null && d.year == null) return false;
  if (d.day != null && d.month == null) return false;
  if (d.year != null && (d.year < 1 || d.year > 2200)) return false;
  if (d.month != null && (d.month < 1 || d.month > 12)) return false;
  if (d.day != null) {
    const max = new Date(Date.UTC(d.year ?? 2000, d.month!, 0)).getUTCDate();
    if (d.day < 1 || d.day > max) return false;
  }
  return true;
}

/** Valor ordenable (aaaammdd) para comparar fechas parciales; null si no hay año. */
export function sortKey(d: PartialDate | null | undefined): number | null {
  if (!d?.year) return null;
  return d.year * 10000 + (d.month ?? 0) * 100 + (d.day ?? 0);
}

export interface LifeSpanInput {
  birthYear: number | null;
  deathYear: number | null;
  /** null: no se sabe si vive. */
  isLiving: boolean | null;
  sex: 'M' | 'F' | 'U';
}

/** "1858–1916", "1964–Vivo", "1836–Fallecida", "Fallecido"; si no se sabe si vive, "1934–?" o "Sin datos de vida". */
export function lifeSpan({ birthYear, deathYear, isLiving, sex }: LifeSpanInput): string {
  if (isLiving === null && !deathYear) return birthYear ? `${birthYear}–?` : 'Sin datos de vida';
  const living = sex === 'F' ? 'Viva' : 'Vivo';
  const dead = sex === 'F' ? 'Fallecida' : 'Fallecido';
  const end = isLiving ? living : deathYear ? String(deathYear) : dead;
  if (!birthYear) return isLiving ? living : deathYear ? `–${deathYear}` : dead;
  return `${birthYear}–${end}`;
}
